import { useState } from "react";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";

const MIN_DAYS_OPTIONS = [
  { value: "0", label: "Aujourd'hui (pas de marge)" },
  { value: "1", label: "1 jour" },
  { value: "2", label: "2 jours" },
  { value: "3", label: "3 jours" },
  { value: "5", label: "5 jours" },
  { value: "7", label: "1 semaine" },
  { value: "14", label: "2 semaines" },
  { value: "21", label: "3 semaines" },
  { value: "30", label: "1 mois" },
];

const SAFETY_MARGIN_OPTIONS = [
  { value: "0", label: "Pas de marge" },
  { value: "0.5", label: "30 minutes" },
  { value: "1", label: "1 heure" },
  { value: "1.5", label: "1h30" },
  { value: "2", label: "2 heures" },
  { value: "3", label: "3 heures" },
  { value: "4", label: "4 heures" },
];

const MAX_PER_DAY_OPTIONS = [
  { value: "0", label: "Illimité" },
  { value: "2", label: "2 interventions" },
  { value: "3", label: "3 interventions" },
  { value: "4", label: "4 interventions" },
  { value: "5", label: "5 interventions" },
  { value: "6", label: "6 interventions" },
  { value: "8", label: "8 interventions" },
  { value: "10", label: "10 interventions" },
  { value: "12", label: "12 interventions" },
  { value: "15", label: "15 interventions" },
];

export const PriseDeRDV = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const updateOrgData = useDataStore((state) => state.updateOrgData);

  const [minDaysAhead, setMinDaysAhead] = useState(
    String(orgData?.config?.booking_min_days_ahead ?? 1),
  );
  const [maxPerDay, setMaxPerDay] = useState(
    String(orgData?.config?.booking_max_per_day ?? 0),
  );
  const [safetyMargin, setSafetyMargin] = useState(
    String(orgData?.config?.booking_safety_margin_hours ?? 1),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!orgData) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateOrgData(orgData.id, {
        config: {
          booking_min_days_ahead: parseInt(minDaysAhead),
          booking_max_per_day: parseInt(maxPerDay),
          booking_safety_margin_hours: parseFloat(safetyMargin),
        },
      });
      showNotification({ message: "Modifications enregistrées avec succès", type: "success" });
    } catch {
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({ message: errorMessage, type: "error" });
    }
    setIsSaving(false);
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Prise de RDV</Text1>
          <Text2>Configuration du widget de réservation en ligne.</Text2>
          <Text3>
            Gérez les paramètres de réservation en ligne accessibles par vos clients
            depuis votre site web.
          </Text3>
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Délai minimum de réservation</div>
          <div className="text-h4 text-neutral-400">
            Nombre de jours minimum entre aujourd'hui et le premier créneau disponible.
            Par exemple, "2 jours" signifie que les clients ne pourront pas réserver avant après-demain.
          </div>
          <DropDown
            label="Marge avant premier créneau"
            value={minDaysAhead}
            options={MIN_DAYS_OPTIONS}
            onSelect={(v) => setMinDaysAhead(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Capacité journalière</div>
          <div className="text-h4 text-neutral-400">
            Nombre maximum d'interventions planifiées par jour. Au-delà de cette limite,
            la journée sera marquée comme complète dans le widget de réservation.
            Cela permet de garder de la marge pour les interventions urgentes.
          </div>
          <DropDown
            label="Maximum d'interventions par jour"
            value={maxPerDay}
            options={MAX_PER_DAY_OPTIONS}
            onSelect={(v) => setMaxPerDay(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Marge de sécurité (restitution)</div>
          <div className="text-h4 text-neutral-400">
            Temps supplémentaire ajouté à la durée estimée de l'intervention pour calculer
            l'heure de restitution la plus tôt possible. Par exemple, si l'intervention dure 1h30
            et la marge est de 1h, le client ne pourra pas choisir une restitution avant 2h30 après le dépôt.
          </div>
          <DropDown
            label="Marge après intervention"
            value={safetyMargin}
            options={SAFETY_MARGIN_OPTIONS}
            onSelect={(v) => setSafetyMargin(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2">
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            onClick={handleSave}
            disabled={isSaving || !isOwnerOrAdmin(userData)}
          />
        </div>
      </div>
    </div>
  );
};
